/**
 * Maps admission-species i18n keys → EU pictogram files in /public/animals.
 * No entry = no official pictogram → callers show text only.
 */
const SPECIES_PICTOGRAM: Record<string, string> = {
  'enum.admission_species.de_brieftaube': 'pigeon',
  'enum.admission_species.de_ente': 'duck',
  'enum.admission_species.de_fisch_forelle': 'fish',
  'enum.admission_species.de_fisch_forelle_setzling_1': 'fish',
  'enum.admission_species.de_fisch_karpfen': 'fish',
  'enum.admission_species.de_fische': 'fish',
  'enum.admission_species.de_fische_essbar': 'fish',
  'enum.admission_species.de_fuchs': 'fox',
  'enum.admission_species.de_gans': 'goose',
  'enum.admission_species.de_hase': 'rabbit',
  'enum.admission_species.de_huhn': 'chicken',
  'enum.admission_species.de_huhn_junghenne': 'chicken',
  'enum.admission_species.de_huhn_kueken': 'chicken',
  'enum.admission_species.de_huhn_legehenne_zuchthenne': 'chicken',
  'enum.admission_species.de_huhn_masthaehnchen_broiler': 'chicken',
  'enum.admission_species.de_hund': 'dog',
  'enum.admission_species.de_hundewelpen': 'dog',
  'enum.admission_species.de_kaninchen': 'rabbit',
  'enum.admission_species.de_kaninchen_nicht_lebensmittelliefernd': 'rabbit',
  'enum.admission_species.de_katze': 'cat',
  'enum.admission_species.de_katzenwelpen': 'cat',
  'enum.admission_species.de_meerschweinchen': 'guinea-pig',
  'enum.admission_species.de_papageienvoegel': 'parrot',
  'enum.admission_species.de_pferd': 'horse',
  'enum.admission_species.de_pferd_fohlen': 'horse',
  'enum.admission_species.de_pferd_nicht_zur_lebensmittelproduktion': 'horse',
  'enum.admission_species.de_pferd_pony': 'horse',
  'enum.admission_species.de_pferd_saugfohlen': 'horse',
  'enum.admission_species.de_pferd_stute': 'horse',
  'enum.admission_species.de_pute': 'turkey',
  'enum.admission_species.de_pute_broiler': 'turkey',
  'enum.admission_species.de_pute_puten_truthuehner': 'turkey',
  'enum.admission_species.de_rind': 'cow',
  'enum.admission_species.de_rind_faerse': 'cow',
  'enum.admission_species.de_rind_kalb': 'cow',
  'enum.admission_species.de_rind_kalb_jungrind': 'cow',
  'enum.admission_species.de_rind_kuh_laktierend': 'cow',
  'enum.admission_species.de_rind_kuh_milchkuh': 'cow',
  'enum.admission_species.de_rind_mastrind': 'cow',
  'enum.admission_species.de_rind_saugkalb': 'cow',
  'enum.admission_species.de_schaf': 'sheep',
  'enum.admission_species.de_schaf_lamm': 'sheep',
  'enum.admission_species.de_schaf_sauglamm': 'sheep',
  'enum.admission_species.de_schlange': 'snake',
  'enum.admission_species.de_schwein': 'pig',
  'enum.admission_species.de_schwein_laeufer_ferkel': 'pig',
  'enum.admission_species.de_schwein_mastschwein_absatzferkel': 'pig',
  'enum.admission_species.de_schwein_mastschwein_masteber': 'pig',
  'enum.admission_species.de_schwein_saugferkel': 'pig',
  'enum.admission_species.de_schwein_zuchteber': 'pig',
  'enum.admission_species.de_schwein_zuchtsau': 'pig',
  'enum.admission_species.de_taube': 'pigeon',
  'enum.admission_species.de_ziege': 'goat',
  'enum.admission_species.de_ziege_ziegenlamm_kitz': 'goat',
  'enum.admission_species.de_zierfische': 'fish',
}

const EXTRA_ALIASES: Record<string, string> = {
  // EN (file is cow.svg / guinea-pig.svg, not cattle.svg)
  'cattle': 'cow',
  'beef': 'cow',
  'guinea pig': 'guinea-pig',
  'carrier pigeon': 'pigeon',
  // FR (hybrid picks one locale label, e.g. "Cheval")
  'chat': 'cat',
  'cheval': 'horse',
  'chien': 'dog',
  'poule': 'chicken',
  'poulet': 'chicken',
  'vache': 'cow',
  'bovin': 'cow',
  'boeuf': 'cow',
  'porc': 'pig',
  'mouton': 'sheep',
  'chevre': 'goat',
  'lapin': 'rabbit',
  'dinde': 'turkey',
  'canard': 'duck',
  'oie': 'goose',
  'poisson': 'fish',
  'renard': 'fox',
  'serpent': 'snake',
  'perroquet': 'parrot',
  'cobaye': 'guinea-pig',
}

const LABEL_LOOKUP: Record<string, string> = { ...EXTRA_ALIASES }
for (const [key, file] of Object.entries(SPECIES_PICTOGRAM)) {
  LABEL_LOOKUP[file] = file
  const suffix = key.slice('enum.admission_species.de_'.length)
  LABEL_LOOKUP[suffix] = file
  LABEL_LOOKUP[suffix.split('_')[0]] = file
}

/** Pictogram URL for PDP enum keys or hybrid-search labels; undefined = text only. */
export function getSpeciesPictogramSrc(speciesKey: string): string | undefined {
  const fromEnum = SPECIES_PICTOGRAM[speciesKey]
  if (fromEnum) return `/animals/${fromEnum}.svg`

  const normalized = speciesKey.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').trim()
  if (!normalized) return undefined

  const file = LABEL_LOOKUP[normalized.replace(/\s+/g, '-')]
    ?? LABEL_LOOKUP[normalized]
    ?? LABEL_LOOKUP[normalized.split(/[\s,(/]+/).find(Boolean) ?? '']

  return file ? `/animals/${file}.svg` : undefined
}
